/**
 * Client-side password gate for the coming-soon site.
 *
 * The real password is never compiled into the bundle. Instead we ship a
 * SHA-256 hash via `VITE_PASSWORD_HASH` (set in .env.production at build time
 * or in Cloudflare Pages env vars). At runtime we hash whatever the user types
 * and compare. This is "secret-screen" security — fine for a coming-soon
 * page but not a substitute for real auth on a public route that exposes data.
 *
 * Update the password by:
 *   echo -n "<new-password>" | shasum -a 256
 * and pasting the hex digest into VITE_PASSWORD_HASH.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

// Default hash matches the dev password "intraalpha" — overridden at build time
// via VITE_PASSWORD_HASH for production. Verify: `echo -n intraalpha | shasum -a 256`.
const DEFAULT_HASH =
  "27407b3965373e434c21cd8e3eed2826efa8cfe9634731b1e8dfc318162a2561"; // SHA-256("intraalpha")

const passwordHash =
  (import.meta.env.VITE_PASSWORD_HASH as string | undefined) ?? DEFAULT_HASH;

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function checkPassword(input: string): Promise<boolean> {
  const hex = await sha256Hex(input);
  return hex === passwordHash;
}

interface AuthState {
  unlocked: boolean;
  unlock: () => void;
  lock: () => void;
}

/**
 * `unlocked` persists across browser sessions in localStorage. The persist
 * key is intentionally unobvious — not a security boundary, but reduces the
 * chance a casual observer notices the gate exists.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      unlocked: false,
      unlock: () => set({ unlocked: true }),
      lock: () => set({ unlocked: false }),
    }),
    { name: "ia.session.v1" },
  ),
);
